import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Stack, Box } from '@mui/material'

import { useNavigate } from 'router'
import Marker, { Label, Chip } from 'packages/wimap/Indoor/Markers'
import BuildingMap from 'packages/wimap/Indoor'
import usePopup from 'packages/wimap/Indoor/use-popup'
import Expand from 'packages/wimap/Controls/Expand'
import { OPERATION_IMAGE } from '../../../operations-gql/queries'
import { deserializeDevice, availableToColor } from '../../../helpers'
import useSite from '../use-site'
import PopupContent from './PopupContent'
import LoaderMap from 'components/LoaderMap'
import { useMap } from 'packages/wimap/Indoor/IndoorProvider'
import IndoorUnavailableModal from './IndoorUnavailableModal'
import Heatmap from '../Heatmap'

const Markers = ({ devices, isSiteDown }) => {
  const navigate = useNavigate()
  const { focusId, buildingId, floorId, deviceId } = useParams()

  const handleDetailsClick = (device) => {
    navigate({
      pathname: `/monitoring/map/${focusId}/${buildingId}/${floorId}/${device.uuid}`,
    })
  }

  const { onMarkerLeave, onMarkerEnter } = usePopup({ 
    PopupContent,
    onDetailsClick: handleDetailsClick,
    isSiteDown
  })
  const { centerOnMarker } = useMap()

  const sortedDevices = useMemo(() => {
    return [...devices].sort((a, b) => {
      if (a.uuid === deviceId) return 1
      if (b.uuid === deviceId) return -1
      return 0
    })
  }, [devices, deviceId])

  const handleMarkerEnter = (markerOrDevice) => {
    if (typeof markerOrDevice === 'string') {
      const device = devices.find(device => {
        if (device.markers) {
          return device.markers.findIndex((marker) => marker.id === markerOrDevice) > -1
        }
        return false
      })
      if (!device) {
        return
      }
      const marker = device.markers.find(marker => marker.id === markerOrDevice)
      if (marker) {
        onMarkerEnter(marker)
      }
    } else if (markerOrDevice && typeof markerOrDevice === 'object') {
      const markerData = {
        ...markerOrDevice,
        id: markerOrDevice.uuid,
        name: markerOrDevice.label,
        device: markerOrDevice
      }
      onMarkerEnter(markerData)
    }
  }

  return sortedDevices.map(device => (
    <React.Fragment key={device.uuid}>
      <Marker
        device={device}
        selected={deviceId === device.uuid}
        onMarkerLeave={onMarkerLeave}
        onMarkerEnter={handleMarkerEnter}
        onMarkerClick={(device) => {
          const marker = device.markers?.[0]
          const position = marker?.device?.position || marker?.position || marker?.pos || device.position
          
          if (centerOnMarker && position?.x && position?.y) {
            centerOnMarker(position)
          }
          
          handleDetailsClick(device)
        }}
        isSiteDown={isSiteDown}
      />
      {!(device.children && device.children.length > 0) && (
        <>
          <Label
            device={device}
            chipColor={
              device.markers && device.markers.length > 0 && ['works', 'hibernation'].includes(device.available_level) 
                ? (isSiteDown ? '#7A8096' : availableToColor(device.markers[0].is_reachable ? 'up' : 'down'))
                : null
            }
          />
          {
            device.type === 'ap' && ['up', 'degraded'].includes(device.available_level) && !isSiteDown && <Chip device={device} pulse={device.uuid === deviceId} />
          }
        </>
      )}
    </React.Fragment>
  ))
}

const IndoorMap = ({isExpanded, onToggleExpand, sites}) => {
  const { floorId, deviceId, focusId } = useParams()
  const { loading, mapEquipments: equipments } = useSite(focusId)
  const [{ works, planned, hibernation }, setFilters] = useState({ planned: true, works: true, hibernation: true })
  const { loading: loadingImg, data: dataImg } = useQuery(OPERATION_IMAGE, { variables: { id: floorId } })
  const [showSiteDownModal, setShowSiteDownModal] = useState(true)
  const [showHeatmap, setShowHeatmap] = useState(false)
  const isSiteDown = useMemo(() => {
    const currentSite = sites.find(({ sf_id }) => sf_id === focusId)
    return currentSite && currentSite.equipment.available_level === 'down'
  }, [sites, focusId])

  const lastSeen = useMemo(() => {
    const currentSite = sites.find(({ sf_id }) => sf_id === focusId)
    return currentSite?.equipment?.last_seen
  }, [sites, focusId])

  const devices = useMemo(
    () =>
      equipments
        .filter(({ model }) => !['tv', 'chromecast'].includes(model?.type))
        .filter(({ status }) => status !== 'spare')
        .filter(({ status }) => works || status !== 'works')
        .filter(({ status }) => planned || status !== 'planned')
        .filter(({ status }) => hibernation || status !== 'hibernation')
        .map((device) => {
          return deserializeDevice({ device, isSiteDown })
        }),
    [equipments, works, hibernation, planned, isSiteDown],
  )

  if (loading || loadingImg) {
    return (
      <Stack sx={{
        bgcolor: '#F0F5F9',
        flexGrow: 1,
        justifyContent: 'center',
        ...(isExpanded && !deviceId && {
          borderRadius: '24px'
        }),
        ...(isExpanded && deviceId && {
          borderRadius: '24px'
        }),
        ...(!isExpanded && deviceId && {
          borderTopRightRadius: '24px',
          borderBottomRightRadius: '24px'
        }),
        ...(!isExpanded && !deviceId && {
          borderTopRightRadius: '24px',
          borderBottomRightRadius: '24px'
        })
      }}>
        <LoaderMap />
      </Stack>
    )
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', isolation: 'isolate'}}>
      <BuildingMap
        canSeeHeatmap={devices.filter(({type}) => type === 'ap').length <= 20}
        showHeatmap={showHeatmap}
        floors={{}}
        breadcrumbProps={{
          showBreadcrumb: false,
        }}
        image={dataImg ? dataImg.image : { width: 500, height: 500 }}
        onFloorChange={() => null}
        onHeatmapClick={() => {
          setShowHeatmap(!showHeatmap)
        }}
        legendProps={{
          onStateFiltersChange: (name, value) => {
            setFilters(prev => ({ ...prev, [name]: value }))
          },
          mapsView: 'building'
        }}
        defaultGrayScale={true}
        hasOpenCard={!!deviceId}
      >
        {
          showHeatmap
            ? <Heatmap devices={devices} image={dataImg?.image} />
            : <Markers devices={devices} isSiteDown={isSiteDown} />
        }
      </BuildingMap>
      
      {isSiteDown && showSiteDownModal && (
        <Box
          onClick={() => setShowSiteDownModal(false)}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            zIndex: 100,
            pointerEvents: 'all'
          }}
        >
          <IndoorUnavailableModal 
            lastSeen={lastSeen}
            onClose={() => setShowSiteDownModal(false)}
          />
        </Box>
      )}
      
      <Expand 
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        sx={{
          position: 'absolute',
          top: 24,
          left: 24,
          zIndex: 10,
          mt: 0,
          ml: 0
        }}
      />
    </Box>
  )
}

export default IndoorMap